import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { useCallback, useContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setSpinnerImmersive,
  setSpinnerTourMode
} from '@/vdp/VehicleDetailsContainer/actions';
import GlobalContext from '~/context/vdp/GlobalContext';
import { getExperimentLoaded } from '~/selectors';
import useForceError from './useForceError';
import useSpinnerSize from './useSpinnerSize';
import HeroContext from '../HeroContext';
import { calculateSpinnerHeight } from '../utilities';

export default function useSpinnerConfig() {
  const { trackEvent } = useContext(AnalyticsContext);
  const {
    headerHeight,
    setOpenImmersive,
    setOpenHotspot,
    spinnerReady: { setData: setSpinnerReady }
  } = useContext(GlobalContext);
  const {
    setHotspotSelected,
    setSpinnerEventHandlers,
    setSpinnerOverflow,
    setViewingInterior
  } = useContext(HeroContext);
  const areExperimentsLoaded = useSelector(getExperimentLoaded);
  const dispatch = useDispatch();
  const forceError = useForceError();

  const spinnerSize = useSpinnerSize();

  const getHelpers = useCallback(
    ({ openImmersive = () => {}, openHotspot = () => {} }) => {
      setOpenImmersive(() => openImmersive);
      setOpenHotspot(() => openHotspot);
    },
    [setOpenImmersive, setOpenHotspot]
  );

  return useMemo(
    () => ({
      autoResizing: true,
      callbacks: { trackEvent },
      didLoadExperiments: areExperimentsLoaded,
      eventHandlers: setSpinnerEventHandlers,
      eventListener: event => {
        if (forceError) forceError();

        switch (event.type) {
          case 'ui/setSpinnerReady':
          case 'spinner/ui/SET_SPINNER_READY':
            setSpinnerReady(true);
            break;
          case 'spinner/ui/SET_SPINNER_OPENED':
            break;
          case 'spinner/ui/SET_EXTERIOR_FOCUS':
            setViewingInterior(event.payload.currentView === 'INT');
            break;
          case 'spinner/ui/HOTSPOT_SELECTED':
            setHotspotSelected(Boolean(event.payload));
            break;
          case 'ui/setImmersiveMode':
          case 'spinner/ui/SET_IMMERSIVE_MODE':
            dispatch(setSpinnerImmersive(event.payload));
            break;
          case 'spinner/ui/SET_TOUR_FOCUS':
            dispatch(setSpinnerTourMode(Boolean(event.payload)));
            break;
          case 'spinner/ui/RESIZE_APP_CONTAINER':
            const expected = calculateSpinnerHeight(headerHeight);
            const { height: actual } = event.payload;

            setSpinnerOverflow(actual > expected);
            break;
          default:
        }
      },
      customDims: spinnerSize.height ? { height: spinnerSize.height } : null,
      customFocus: spinnerSize.focus ? { y: spinnerSize.focus } : null, // Only needed by 64f
      showHeader: false,
      getHelpers
    }),
    [
      areExperimentsLoaded,
      dispatch,
      forceError,
      getHelpers,
      headerHeight,
      setHotspotSelected,
      setSpinnerEventHandlers,
      setSpinnerOverflow,
      setSpinnerReady,
      setViewingInterior,
      spinnerSize.focus,
      spinnerSize.height,
      trackEvent
    ]
  );
}
